import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useUI } from '../../context/UIContext';
import { useLicense } from '../../context/LicenseContext';

const Sidebar = () => {
    const location = useLocation();
    const { hasPermission, logout } = useAuth();
    const { getSetting } = useSettings();
    const { isSidebarCollapsed } = useUI();
    const { hasModule } = useLicense();

    const isMultiSite = getSetting('multi_site_mode', false);

    const sections = [
        {
            title: 'Menu',
            items: [
                { name: 'Tableau de bord', icon: 'dashboard', path: '/dashboard' },
            ]
        },
        {
            title: 'Logistique',
            items: [
                { name: 'Entrepôts', icon: 'warehouse', path: '/warehouses', visible: isMultiSite, permission: 'wh.view', module: 'mod_warehouse' },
                { name: 'Stocks', icon: 'package_2', path: '/stocks', permission: 'stock.scanner', module: 'mod_stock' },
                { name: 'Inventaire', icon: 'inventory_2', path: '/inventory', permission: 'inv.view', module: 'mod_inventory' },
                { name: 'Étiquettes', icon: 'qr_code_2', path: '/inventory/print-labels', permission: 'inv.manage', module: 'mod_etiquette' },
            ]
        },
        {
            title: 'Achats & Flux',
            items: [
                { name: 'Fournisseurs', icon: 'local_shipping', path: '/suppliers', permission: 'sup.manage', module: 'mod_supplier' },
                { name: 'Commandes Achat', icon: 'receipt_long', path: '/purchase-orders', permission: 'order.view', module: 'mod_purchase' },
                { name: 'Réappro. Smart', icon: 'quick_reorder', path: '/smart-order', permission: 'order.create', module: 'mod_reappro' },
            ]
        },
        {
            title: 'Administration',
            items: [
                { name: 'Rapports', icon: 'monitoring', path: '/analytics', permission: 'inv.view', module: 'mod_report' },
                { name: 'Utilisateurs', icon: 'group', path: '/users', permission: 'users.manage' },
                { name: 'Ma Société', icon: 'domain', path: '/admin/company', permission: 'settings.manage' },
                { name: 'Configuration', icon: 'settings', path: '/admin/settings', permission: 'settings.manage' },
            ]
        },
        {
            title: 'Support',
            items: [
                { name: 'Aide & Support', icon: 'help', path: '/help' },
            ]
        }
    ];

    const renderMenuItem = (item) => {
        if (item.visible === false) return null;
        if (item.permission && !hasPermission(item.permission)) return null;
        if (item.module && !hasModule(item.module)) return null;

        const isActive = location.pathname === item.path;

        return (
            <Link
                key={item.path}
                to={item.path}
                title={isSidebarCollapsed ? item.name : ''}
                className={`flex items-center gap-3 rounded-xl transition-all duration-300 group relative
                    ${isSidebarCollapsed ? 'px-0 justify-center h-12 w-12 mx-auto' : 'px-3 py-2.5'}
                    ${isActive
                        ? 'bg-primary/10 text-primary font-extrabold shadow-sm shadow-primary/5'
                        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-slate-200'
                    }`}
            >
                <span className={`material-symbols-outlined transition-all duration-300
                    ${isActive ? 'scale-110 font-bold' : 'group-hover:scale-110'}
                    ${isSidebarCollapsed ? 'text-2xl' : 'text-[22px]'}
                `}>
                    {item.icon}
                </span>
                {!isSidebarCollapsed && (
                    <span className="text-sm tracking-tight whitespace-nowrap animate-fade-in">
                        {item.name}
                    </span>
                )}

                {/* Active Indicator (Dot) when collapsed */}
                {isSidebarCollapsed && isActive && (
                    <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full shadow-lg shadow-primary/50" />
                )}
            </Link>
        );
    };

    return (
        <aside className={`hidden lg:flex flex-col border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-background-dark h-full overflow-hidden transition-all duration-300 ease-in-out
            ${isSidebarCollapsed ? 'w-20' : 'w-64'}
        `}>
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 custom-scrollbar">
                <div className={`space-y-8 ${isSidebarCollapsed ? 'space-y-6' : ''}`}>
                    {sections.map((section, idx) => {
                        const filteredItems = section.items.filter(item => {
                            if (item.visible === false) return false;
                            if (item.permission && !hasPermission(item.permission)) return false;
                            if (item.module && !hasModule(item.module)) return false;
                            return true;
                        });

                        if (filteredItems.length === 0) return null;

                        return (
                            <div key={idx} className="space-y-2">
                                {section.title && !isSidebarCollapsed && (
                                    <div className="px-3 mb-2">
                                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] animate-fade-in">
                                            {section.title}
                                        </p>
                                    </div>
                                )}
                                {isSidebarCollapsed && (
                                    <div className="border-b border-slate-100 dark:border-slate-800/50 mx-2 mb-4" />
                                )}
                                <div className="space-y-1">
                                    {filteredItems.map(item => renderMenuItem(item))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className={`p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 transition-all duration-300
                ${isSidebarCollapsed ? 'px-0' : 'px-4'}
            `}>
                <button
                    onClick={logout}
                    title={isSidebarCollapsed ? "Déconnexion" : ""}
                    className={`flex items-center gap-3 rounded-xl text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-all font-bold text-sm group
                        ${isSidebarCollapsed ? 'justify-center h-12 w-12 mx-auto' : 'px-3 py-2.5 w-full'}
                    `}
                >
                    <span className="material-symbols-outlined font-bold group-hover:rotate-12 transition-transform">logout</span>
                    {!isSidebarCollapsed && <span className="animate-fade-in">Déconnexion</span>}
                </button>
            </div>
        </aside>
    );
};

export default Sidebar;
